import React, { useEffect, useState, useRef } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import PageHeader from '../../components/PageHeader/PageHeader.tsx';
import { useTranslation } from 'react-i18next';
import HardwareTable, { type HardwareTableHandles } from './HardwareTable';
import AleaFilterTags from '../../components/AleaFilterTags/AleaFilterTags';
import SearchBar from '../../components/SearchBar/SearchBar';
import { useAuth } from '../../context/AuthContext.tsx';
import { debounce } from 'lodash';
import { UserRole } from '../../constants/UserRoles.ts';
import AleaListFilter from '../../components/AleaListFilter/AleaListFilter.tsx';
import { AleaSelectedTags } from '../../components/AleaSelectedTag';
import { useLocation, useNavigate } from 'react-router-dom';
import { ICareHome } from '../../interfaces/CareHome/ICareHome.ts';
import AleaPagination from '../../components/AleaPagination/AleaPagination.tsx';
import { usePerPageStorage } from '../../hooks/usePerPageStorage';
import AvailableClipsTable, {
  type AvailableClipsTableHandles,
} from './AvailableClipsTable.tsx';
import AleaConfirmModal from '../../components/AleaModal/AleaConfirmModal';
import { usePageFiltersStorage } from '../../hooks/usePageFiltersStorage';
import {
  useHardwareAvailableClipsQuery,
  useHardwareCareHomesQuery,
  useHardwareCareHomeStatsQuery,
  useHardwareGatewaysQuery,
} from './hardwarePage.queries.ts';
import { useHardwarePageMutations } from './hardwarePage.mutations.ts';

const HardwarePage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { isRestoring } = usePageFiltersStorage();
  const { perPage: storedPerPage, updatePerPage } = usePerPageStorage();
  const {
    invalidateHardwareAvailableClips,
    invalidateHardwareCareHomeStats,
    invalidateHardwareGateways,
  } = useHardwarePageMutations();
  const pendingNavigateRef = useRef<string | null>(null);
  const hardwareTableRef = useRef<HardwareTableHandles | null>(null);
  const availableClipsTableRef = useRef<AvailableClipsTableHandles | null>(null);

  // Get URL parameters
  const params = new URLSearchParams(location.search);
  const selectedCarehome = params.get('carehome_id');
  const orderBy = params.get('orderBy') || undefined;
  const sortOrder =
    (params.get('sortOrder') as 'asc' | 'desc' | undefined) || undefined;
  const currentPage = parseInt(params.get('page') || '1', 10);

  // Use localStorage value if URL doesn't have perPage
  const urlPerPage = params.get('perPage');
  const perPage = urlPerPage ? parseInt(urlPerPage, 10) : storedPerPage;

  const urlQueryParam = params.get('query') || '';
  const urlSelectedTag = params.get('tag') || 'assigned';

  // Modal states for unsaved changes warning
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] =
    useState(false);
  const [isAnyModalOpen, setIsAnyModalOpen] = useState(false);

  // State for search and filters - initialize from URL
  const [searchQuery, setSearchQuery] = useState(urlQueryParam);
  const [selectedTag, setSelectedTag] = useState(urlSelectedTag);

  useEffect(() => {
    if (isRestoring) return;
    if (storedPerPage === 10) return;

    const newParams = new URLSearchParams(location.search);
    if (newParams.get('perPage')) return;

    newParams.set('perPage', storedPerPage.toString());
    navigate(`${location.pathname}?${newParams.toString()}`, { replace: true });
  }, [isRestoring, location.pathname, location.search, navigate, storedPerPage]);

  // Update states when URL changes
  useEffect(() => {
    const nextParams = new URLSearchParams(location.search);
    setSearchQuery(nextParams.get('query') || '');
    setSelectedTag(nextParams.get('tag') || 'assigned');
  }, [location.search]);

  const isCareHomeManager = user?.role === UserRole.CAREHOME_MANAGER;
  const isOrganisationManager = user?.role === UserRole.ORGANISATION_MANAGER;

  const careHomeIdForData = isCareHomeManager ? user?.carehome?.id : selectedCarehome || undefined;
  const organisationIdForData = isOrganisationManager ? user?.organisation?.id : undefined;

  const careHomesQuery = useHardwareCareHomesQuery(
    organisationIdForData,
    !isRestoring && isOrganisationManager
  );

  const carehomes: ICareHome[] = careHomesQuery.data?.items ?? [];

  const gatewaysQuery = useHardwareGatewaysQuery(
    {
      page: currentPage,
      perPage,
      tag: (urlSelectedTag === 'available-clips' ? 'assigned' : urlSelectedTag) as
        | 'assigned'
        | 'free'
        | 'deactivated',
      query: urlQueryParam || undefined,
      orderBy,
      sortOrder,
      carehomeId: isCareHomeManager
        ? user?.carehome?.id
        : isOrganisationManager
          ? selectedCarehome || undefined
          : undefined,
      organisationId: isOrganisationManager ? user?.organisation?.id : undefined,
    },
    !isRestoring && urlSelectedTag !== 'available-clips'
  );

  const clipsCareHomeId =
    user?.role === UserRole.ORGANISATION_MANAGER ? selectedCarehome || undefined : user?.carehome?.id;

  const clipsQuery = useHardwareAvailableClipsQuery(
    {
      page: currentPage,
      perPage,
      query: urlQueryParam || undefined,
      orderBy,
      sortOrder,
      carehomeId: clipsCareHomeId,
      userRole: user?.role,
    },
    !isRestoring && urlSelectedTag === 'available-clips'
  );

  const careHomeStatsQuery = useHardwareCareHomeStatsQuery(careHomeIdForData, !isRestoring);

  const gateways = gatewaysQuery.data?.items ?? [];
  const clips = clipsQuery.data?.items ?? [];
  const total =
    urlSelectedTag === 'available-clips'
      ? clipsQuery.data?.total ?? 0
      : gatewaysQuery.data?.total ?? 0;
  const isListLoading =
    urlSelectedTag === 'available-clips'
      ? clipsQuery.isLoading || clipsQuery.isFetching
      : gatewaysQuery.isLoading || gatewaysQuery.isFetching;

  // Generate dynamic description based on stats
  const generateDescription = () => {
    if (!careHomeStatsQuery.data) {
      return null; // Return null when loading
    }

    const careHomeStats = careHomeStatsQuery.data;

    const totalDocks =
      careHomeStats.assigned.charger_gateways +
      careHomeStats.free.charger_gateways +
      careHomeStats.deactivated.charger_gateways;

    const totalClips =
      careHomeStats.assigned.clips +
      careHomeStats.free.clips +
      careHomeStats.deactivated.clips;

    return t('hardwareCount', {
      docksTotal: totalDocks,
      docksAssigned: careHomeStats.assigned.charger_gateways,
      clipsTotal: totalClips,
      clipsAssigned: careHomeStats.assigned.clips,
    });
  };

  const handlePageChange = (page: number) => {
    const newParams = new URLSearchParams(location.search);
    newParams.set('page', page.toString());
    navigate(`${location.pathname}?${newParams.toString()}`);
  };

  const handlePerPageChange = (newPerPage: number) => {
    // Update localStorage first
    updatePerPage(newPerPage);

    // Then update URL
    const newParams = new URLSearchParams(location.search);
    newParams.set('perPage', newPerPage.toString());
    newParams.set('page', '1'); // Go to first page when items per page changes
    navigate(`${location.pathname}?${newParams.toString()}`);
  };

  // Listen to global leave attempts from layout (logo, any link)
  useEffect(() => {
    const handler = (e: Event) => {
      const to = (e as CustomEvent<string>).detail;
      if (!to) return;

      // Check if there are any pending changes in either table
      const hasHardwareTableChanges =
        hardwareTableRef.current?.hasPendingChanges?.();
      const hasAvailableClipsTableChanges =
        availableClipsTableRef.current?.hasPendingChanges?.();

      if (hasHardwareTableChanges || hasAvailableClipsTableChanges) {
        // Show confirm modal
        setIsUnsavedChangesModalOpen(true);
        pendingNavigateRef.current = to;
        return;
      }

      // No pending changes, allow navigation
      navigate(to);
    };
    window.addEventListener(
      'attempt-leave-from-hardware',
      handler as EventListener
    );
    return () =>
      window.removeEventListener(
        'attempt-leave-from-hardware',
        handler as EventListener
      );
  }, [navigate]);

  // Handle modal actions for unsaved changes
  const handleSaveAndProceed = async () => {
    setIsUnsavedChangesModalOpen(false);

    // Save changes in both tables if they have pending changes
    if (hardwareTableRef.current?.savePendingChanges) {
      await hardwareTableRef.current.savePendingChanges();
    }
    if (availableClipsTableRef.current?.savePendingChanges) {
      await availableClipsTableRef.current.savePendingChanges();
    }

    // Navigate to pending route
    if (pendingNavigateRef.current) {
      navigate(pendingNavigateRef.current);
      pendingNavigateRef.current = null;
    }
  };

  const handleDiscardAndProceed = () => {
    setIsUnsavedChangesModalOpen(false);
    setIsAnyModalOpen(true); // Keep header overlay active for progress modals

    // Discard changes in both tables
    if (hardwareTableRef.current?.discardPendingChanges) {
      hardwareTableRef.current.discardPendingChanges();
    }
    if (availableClipsTableRef.current?.discardPendingChanges) {
      availableClipsTableRef.current.discardPendingChanges();
    }

    // Wait for progress modal to show, then navigate
    setTimeout(() => {
      if (pendingNavigateRef.current) {
        navigate(pendingNavigateRef.current);
        pendingNavigateRef.current = null;
      }

      // Reset modal state after navigation
      setTimeout(() => setIsAnyModalOpen(false), 100);
    }, 2500); // Wait for progress modal to complete (2 seconds + buffer)
  };

  // Handle progress modal state changes from child components
  const handleHardwareTableProgressModalChange = (isOpen: boolean) => {
    setIsAnyModalOpen(isOpen);
  };

  const handleAvailableClipsTableProgressModalChange = (isOpen: boolean) => {
    setIsAnyModalOpen(isOpen);
  };

  // Notify header about modal state changes
  useEffect(() => {
    const event = new CustomEvent('alea-global-modal-open', {
      detail: isUnsavedChangesModalOpen || isAnyModalOpen,
    });
    window.dispatchEvent(event);
  }, [isUnsavedChangesModalOpen, isAnyModalOpen]);

  const handleCloseModal = () => {
    setIsUnsavedChangesModalOpen(false);
    setIsAnyModalOpen(true); // Keep header overlay active for progress modals

    // When user closes modal (X button), discard changes and navigate
    if (hardwareTableRef.current?.discardPendingChanges) {
      hardwareTableRef.current.discardPendingChanges();
    }
    if (availableClipsTableRef.current?.discardPendingChanges) {
      availableClipsTableRef.current.discardPendingChanges();
    }

    // Wait for progress modal to show, then navigate
    setTimeout(() => {
      if (pendingNavigateRef.current) {
        navigate(pendingNavigateRef.current);
        pendingNavigateRef.current = null;
      }

      // Reset modal state after navigation
      setTimeout(() => setIsAnyModalOpen(false), 100);
    }, 2500); // Wait for progress modal to complete (2 seconds + buffer)
  };

  const handleTagSelect = (value: string) => {
    setSelectedTag(value);
    const newParams = new URLSearchParams(location.search);
    newParams.set('tag', value);
    newParams.set('page', '1'); // Reset to page 1 when changing tag
    navigate(`${location.pathname}?${newParams.toString()}`);
  };

  // Debounced search handler
  const debouncedSearch = debounce((query: string) => {
    const newParams = new URLSearchParams(location.search);
    if (query) {
      newParams.set('query', query);
    } else {
      newParams.delete('query');
    }
    newParams.set('page', '1'); // Reset to page 1 when searching
    navigate(`${location.pathname}?${newParams.toString()}`);
  }, 1000);

  const handleSearch = (query: string) => {
    debouncedSearch(query);
  };

  // Handle sorting
  const handleSort = (field: string, order: 'asc' | 'desc') => {
    const newParams = new URLSearchParams(location.search);
    newParams.set('orderBy', field);
    newParams.set('sortOrder', order);
    navigate(`${location.pathname}?${newParams.toString()}`);
  };

  // Dynamic filters for AleaListFilter component based on fetched carehomes
  const filters = {
    rstate: carehomes.map((carehome) => ({
      label: carehome.name,
      value: carehome.name,
      id: carehome.id,
    })),
  };

  // Reset expanded row when tab changes
  useEffect(() => {
    if (hardwareTableRef.current?.resetExpandedRow) {
      hardwareTableRef.current.resetExpandedRow();
    }
    if (availableClipsTableRef.current?.resetExpandedRow) {
      availableClipsTableRef.current.resetExpandedRow();
    }
  }, [selectedTag]);

  const filterTags = [
    { label: t('assigned'), value: 'assigned' },
    { label: t('free'), value: 'free' },
    { label: t('deactivated'), value: 'deactivated' },
    { label: t('availableClips'), value: 'available-clips' },
  ];

  return (
    <Box data-testid="hardware-page">
      <PageHeader
        title={t('hardware')}
        description={generateDescription() || undefined}
      />

      <Box mb={6}>
        <Flex
          justifyContent="space-between"
          alignItems="center"
          flexDirection={{ base: 'column', md: 'row' }}
          gap={{ base: '16px', md: '16px' }}
          flexWrap={'wrap'}
        >
          <AleaFilterTags
            tags={filterTags}
            selectedTags={[selectedTag]}
            onTagSelect={handleTagSelect}
          />
          <Flex
            gap="10px"
            alignItems="center"
            position="relative"
            alignSelf="flex-start"
            width={{ base: '100%', md: 'auto' }}
          >
            <Box w={{ base: '100%', md: '320px' }}>
              <SearchBar
                onClose={() => handleSearch('')}
                onSearch={handleSearch}
                searchQuery={searchQuery}
                isHardwarePage={true}
                height="50px"
              />
            </Box>
            {user?.role === UserRole.ORGANISATION_MANAGER && (
              <AleaListFilter
                filters={filters}
                isSimpleList={true}
                dropdownWidth="450px"
              />
            )}
          </Flex>
        </Flex>
      </Box>

      {/* Selected filter tags */}
      {user?.role === UserRole.ORGANISATION_MANAGER && (
        <AleaSelectedTags filters={filters} />
      )}

      <Box borderRadius="20px" overflow="auto" minHeight={500} bg="white">
        {selectedTag === 'available-clips' ? (
          <AvailableClipsTable
            ref={availableClipsTableRef}
            clips={clips}
            total={total}
            isLoading={isListLoading}
            onSort={handleSort}
            orderBy={orderBy}
            sortOrder={sortOrder}
            onRefresh={invalidateHardwareAvailableClips}
            onProgressModalChange={handleAvailableClipsTableProgressModalChange}
            onStatsRefresh={invalidateHardwareCareHomeStats}
          />
        ) : (
          <HardwareTable
            ref={hardwareTableRef}
            gateways={gateways}
            total={total}
            onRefresh={invalidateHardwareGateways}
            selectedTag={selectedTag}
            isLoading={isListLoading}
            onSort={handleSort}
            orderBy={orderBy}
            sortOrder={sortOrder}
            onProgressModalChange={handleHardwareTableProgressModalChange}
            onStatsRefresh={invalidateHardwareCareHomeStats}
          />
        )}
        <Box textAlign="center">
          <AleaPagination
            currentPage={currentPage}
            totalPages={Math.ceil(total / perPage)}
            total={total}
            perPage={perPage}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
            isLoading={isListLoading}
          />
        </Box>
      </Box>

      {/* Unsaved Changes Modal */}
      <AleaConfirmModal
        isOpen={isUnsavedChangesModalOpen}
        onClose={handleCloseModal}
        onConfirm={handleSaveAndProceed}
        onCancelButtonClick={handleDiscardAndProceed}
        title={t('saveChangesPopup')}
        confirmLabel={t('general.saveChanges')}
        cancelLabel={t('general.cancel')}
      />
    </Box>
  );
};

export default HardwarePage;
